import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, User, Edit, Trash2, Filter, X, Users, Briefcase } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchResources,
  createResource,
  updateResource,
  deleteResource,
  setTypeFilter,
  setLevelFilter,
  setActiveFilter,
  clearFilters,
  clearError,
} from '@/store/slices/resourcesSlice';
import { DataTable } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassSelect,
  GlassPageHeader,
  SectionReveal,
  MetricCard,
  CollapsibleMetricsSection,
  GlassModal,
  GlassConfirmModal,
  GlassLoadingSpinner,
  GlassAlert,
} from '@/design-system';
import ResourceForm from './components/ResourceForm';
import type { Resource, ResourceType, ResourceLevel, Column } from '@/types';

const ResourcesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    entities: resources,
    filters,
    loading,
    error,
  } = useSelector((state: RootState) => state.resources);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    dispatch(fetchResources({
      type: filters.type || undefined,
      level: filters.level || undefined,
      active: filters.active ?? undefined,
    }));
  }, [dispatch, filters.type, filters.level, filters.active]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, dispatch]);

  const handleCreateResource = async (data: Partial<Resource>) => {
    const result = await dispatch(createResource(data));
    if (createResource.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
    }
  };

  const handleUpdateResource = async (data: Partial<Resource>) => {
    if (!selectedResource) return;
    const result = await dispatch(updateResource({ id: selectedResource.id, data }));
    if (updateResource.fulfilled.match(result)) {
      setIsEditModalOpen(false);
      setSelectedResource(null);
    }
  };

  const handleDeleteResource = async () => {
    if (!selectedResource) return;
    const result = await dispatch(deleteResource(selectedResource.id));
    if (deleteResource.fulfilled.match(result)) {
      setIsDeleteModalOpen(false);
      setSelectedResource(null);
    }
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const hasActiveFilters = filters.type || filters.level || filters.active !== null;

  // Calculate metrics
  const activeResources = resources.filter(r => r.isActive).length;
  const employees = resources.filter(r => r.resourceType === 'EMPLOYEE').length;
  const contractors = resources.filter(r => r.resourceType === 'CONTRACTOR' || r.resourceType.includes('GDP')).length;

  const getTypeBadge = (type: ResourceType | null | undefined) => {
    if (!type) {
      return (
        <GlassBadge variant="default" size="sm">
          -
        </GlassBadge>
      );
    }

    const variants: Record<ResourceType, 'info' | 'success' | 'warning' | 'default'> = {
      EMPLOYEE: 'info',
      CONTRACTOR: 'warning',
      GDP_ONSHORE: 'success',
      GDP_OFFSHORE: 'success',
      INTERN: 'default',
    };
    return (
      <GlassBadge variant={variants[type]} size="sm">
        {type.replace('_', ' ')}
      </GlassBadge>
    );
  };

  const getLevelBadge = (level: ResourceLevel | null | undefined) => {
    if (!level || !level.code) {
      return (
        <GlassBadge variant="default" size="sm">
          -
        </GlassBadge>
      );
    }

    const code = level.code;
    const isManager = code.startsWith('M');
    const isSenior = ['T5', 'T6', 'M4', 'M5'].includes(code);

    return (
      <GlassBadge
        variant={isManager ? 'success' : isSenior ? 'info' : 'default'}
        size="sm"
      >
        {level.displayName || code}
      </GlassBadge>
    );
  };

  const typeOptions = [
    { value: '', label: 'All Types' },
    { value: 'EMPLOYEE', label: 'Employee' },
    { value: 'CONTRACTOR', label: 'Contractor' },
    { value: 'GDP_ONSHORE', label: 'GDP Onshore' },
    { value: 'GDP_OFFSHORE', label: 'GDP Offshore' },
    { value: 'INTERN', label: 'Intern' },
  ];

  const levelOptions = [
    { value: '', label: 'All Levels' },
    { value: 'T1', label: 'T1 (Junior)' },
    { value: 'T2', label: 'T2' },
    { value: 'T3', label: 'T3 (Mid)' },
    { value: 'T4', label: 'T4' },
    { value: 'T5', label: 'T5 (Senior)' },
    { value: 'T6', label: 'T6 (Principal)' },
    { value: 'M2', label: 'M2 (Manager)' },
    { value: 'M3', label: 'M3 (Sr. Manager)' },
    { value: 'M4', label: 'M4 (Director)' },
    { value: 'M5', label: 'M5 (VP)' },
    { value: 'GDP', label: 'GDP' },
    { value: 'CONTRACTOR', label: 'Contractor' },
  ];

  const statusOptions = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
  ];

  const columns: Column<Resource>[] = [
    {
      key: 'resourceId',
      header: 'Resource ID',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-[var(--text)] text-[13px] font-mono">{value}</div>
          <div className="text-xs text-[var(--muted)]">
            {row.firstName} {row.lastName}
          </div>
        </div>
      ),
    },
    {
      key: 'resourceType',
      header: 'Type',
      sortable: true,
      render: (value) => getTypeBadge(value as ResourceType),
    },
    {
      key: 'resourceLevel',
      header: 'Level',
      sortable: true,
      render: (_value, row) => getLevelBadge(row.resourceLevel),
    },
    {
      key: 'isActive',
      header: 'Status',
      sortable: true,
      render: (value) => (
        <GlassBadge variant={value ? 'success' : 'default'}>
          {value ? 'Active' : 'Inactive'}
        </GlassBadge>
      ),
    },
    {
      key: '_count',
      header: 'Teams',
      render: (value) => {
        const count = value?.teamAssignments || 0;
        return (
          <div className="flex items-center gap-1">
            <Users className="w-4 h-4 text-[var(--muted)]" />
            <span className="font-medium text-accent-blue">{count}</span>
          </div>
        );
      },
    },
  ];

  const tableActions = [
    {
      label: 'Edit',
      icon: Edit,
      onClick: (resource: Resource) => {
        setSelectedResource(resource);
        setIsEditModalOpen(true);
      },
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (resource: Resource) => {
        setSelectedResource(resource);
        setIsDeleteModalOpen(true);
      },
    },
  ];

  if (loading && resources.length === 0) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading resources..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Resources"
          subtitle="Manage employees, contractors, and team members"
          icon={<User className="w-6 h-6" />}
          iconGradient="emerald"
          className="mb-6"
          actions={
            <div className="flex items-center gap-3">
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                leftIcon={<Filter className="w-4 h-4" />}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
                {hasActiveFilters && (
                  <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-[var(--accent)] rounded-full">
                    {[filters.type, filters.level, filters.active !== null].filter(Boolean).length}
                  </span>
                )}
              </GlassButton>
              <GlassButton
                variant="primary"
                size="sm"
                onClick={() => setIsCreateModalOpen(true)}
                leftIcon={<Plus className="w-4 h-4" />}
              >
                Add Resource
              </GlassButton>
            </div>
          }
        />
      </SectionReveal>

      {/* Metrics Row */}
      <SectionReveal delay={0.1}>
        <CollapsibleMetricsSection storageKey="resources">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Resources"
              value={resources.length}
              icon={<User className="w-5 h-5" />}
              iconColor="emerald"
            />
            <MetricCard
              title="Active"
              value={activeResources}
              icon={<Users className="w-5 h-5" />}
              iconColor="blue"
            />
            <MetricCard
              title="Employees"
              value={employees}
              icon={<Briefcase className="w-5 h-5" />}
              iconColor="violet"
            />
            <MetricCard
              title="Contractors"
              value={contractors}
              icon={<User className="w-5 h-5" />}
              iconColor="amber"
            />
          </div>
        </CollapsibleMetricsSection>
      </SectionReveal>

      {error && (
        <SectionReveal>
          <GlassAlert
            variant="error"
            message={error}
            onClose={() => dispatch(clearError())}
            className="mb-6"
          />
        </SectionReveal>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <SectionReveal delay={0.15}>
          <GlassCard className="mb-6">
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="block text-xs font-medium uppercase tracking-wide text-[var(--muted)]">
                    Resource Type
                  </label>
                  <GlassSelect
                    value={filters.type || ''}
                    onChange={(value) => dispatch(setTypeFilter(value as ResourceType || null))}
                    options={typeOptions}
                    placeholder="Type"
                    fullWidth
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-medium uppercase tracking-wide text-[var(--muted)]">
                    Resource Level
                  </label>
                  <GlassSelect
                    value={filters.level || ''}
                    onChange={(value) => dispatch(setLevelFilter(value || null))}
                    options={levelOptions}
                    placeholder="Level"
                    fullWidth
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-xs font-medium uppercase tracking-wide text-[var(--muted)]">
                    Status
                  </label>
                  <GlassSelect
                    value={filters.active === null ? '' : filters.active ? 'true' : 'false'}
                    onChange={(value) => {
                      dispatch(setActiveFilter(value === '' ? null : value === 'true'));
                    }}
                    options={statusOptions}
                    placeholder="Status"
                    fullWidth
                  />
                </div>
              </div>

              {hasActiveFilters && (
                <div className="flex justify-end mt-4 pt-4 border-t border-white/10">
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    leftIcon={<X className="w-4 h-4" />}
                  >
                    Clear Filters
                  </GlassButton>
                </div>
              )}
            </div>
          </GlassCard>
        </SectionReveal>
      )}

      <SectionReveal delay={0.2}>
        <GlassCard padding="none">
          <div className="max-h-[calc(100vh-350px)] overflow-auto">
            <DataTable
              data={resources}
              columns={columns}
              actions={tableActions}
              searchable
              searchPlaceholder="Search resources..."
              sortable
              pagination
              pageSize={25}
              emptyMessage="No resources found"
            />
          </div>
        </GlassCard>
      </SectionReveal>

      {/* Floating Create button */}
      <button
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
        onClick={() => setIsCreateModalOpen(true)}
        aria-label="Add Resource"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Create Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Add Resource"
        subtitle="Create a new team member or contractor"
        icon={<User className="w-5 h-5" />}
        iconGradient="emerald"
        size="lg"
      >
        <ResourceForm
          onSubmit={handleCreateResource}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={loading}
        />
      </GlassModal>

      {/* Edit Modal */}
      <GlassModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedResource(null);
        }}
        title="Edit Resource"
        subtitle={selectedResource ? `${selectedResource.firstName} ${selectedResource.lastName}` : undefined}
        icon={<Edit className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="lg"
      >
        <ResourceForm
          initialData={selectedResource}
          onSubmit={handleUpdateResource}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedResource(null);
          }}
          isLoading={loading}
        />
      </GlassModal>

      {/* Delete Confirmation Modal */}
      <GlassConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedResource(null);
        }}
        onConfirm={handleDeleteResource}
        title="Delete Resource"
        message={
          selectedResource
            ? `Are you sure you want to delete ${selectedResource.firstName} ${selectedResource.lastName} (${selectedResource.resourceId})? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        isLoading={loading}
      />
    </div>
  );
};

export default ResourcesPage;
